import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Loader2, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { lastMonths } from "@/components/MonthStatusCard";
import { HoursRow, money } from "@/components/hours/HoursRow";
import { fmtHrs, monthLabel } from "@/lib/timesheet";

const sum = (rows, k) => rows.reduce((t, r) => t + (r[k] || 0), 0);

async function downloadCsv(month) {
  const res = await api.get(`/admin/export/monthly-hours.csv?month=${month}`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = `mokora-monthly-hours-${month}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function MonthlyHours() {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0]);
  const [rows, setRows] = useState(null);
  const [openIds, setOpenIds] = useState([]);
  const [emailing, setEmailing] = useState(false);
  const emailNow = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/admin/monthly-hours/email?month=${month}`);
      toast.success(`Monthly hours for ${monthLabel(month)} emailed to ${data.sent} recipient(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmailing(false);
    }
  };
  const idx = months.indexOf(month);
  const toggle = (id) => setOpenIds((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  useEffect(() => {
    setRows(null);
    api.get(`/admin/monthly-hours?month=${month}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [month]);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Monthly Hours</h1>
          <p className="mt-1 text-muted-foreground">Each contractor's hours for the month, ready for invoicing. Click a row for the client and project breakdown.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button data-testid="hours-prev" variant="ghost" size="icon" disabled={idx >= months.length - 1} onClick={() => setMonth(months[idx + 1])} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="hours-month-picker" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="hours-next" variant="ghost" size="icon" disabled={idx <= 0} onClick={() => setMonth(months[idx - 1])} aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
          <Button data-testid="hours-download-csv" variant="outline" className="ml-2 gap-2" disabled={!rows?.length} onClick={() => downloadCsv(month).catch(() => toast.error("Download failed"))}><Download className="h-4 w-4" /> Download CSV</Button>
          <Button data-testid="hours-email-now" variant="outline" className="gap-2" disabled={!rows?.length || emailing} onClick={emailNow}>{emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email Accounts now</Button>
        </div>
      </div>

      <Card className="mt-6 overflow-hidden" data-testid="monthly-hours-card">
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No contractors to show.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contractor</TableHead>
                  <TableHead className="text-right">Standard</TableHead>
                  <TableHead className="text-right">Overtime</TableHead>
                  <TableHead className="text-right">Leave</TableHead>
                  <TableHead className="text-right">Worked</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Invoice no.</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => <HoursRow key={r.user_id} r={r} open={openIds.includes(r.user_id)} onToggle={() => toggle(r.user_id)} />)}
              </TableBody>
              <TableFooter>
                <TableRow data-testid="hours-total-row">
                  <TableCell className="font-bold">Total - {monthLabel(month)}</TableCell>
                  {["standard", "overtime", "leave"].map((k) => <TableCell key={k} className="text-right font-mono tabular">{fmtHrs(sum(rows, k))}</TableCell>)}
                  <TableCell data-testid="hours-total-worked" className="text-right font-mono font-bold tabular">{fmtHrs(sum(rows, "worked"))}</TableCell>
                  <TableCell />
                  <TableCell data-testid="hours-total-amount" className="text-right font-mono font-bold tabular">{money(sum(rows, "amount"))}</TableCell>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">{rows.filter((r) => r.invoice_number).length} of {rows.length} invoices in</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </Card>
      <p className="mt-3 text-xs text-muted-foreground">Amount = worked hours (standard + overtime) x hourly rate. Leave is not charged. Set rates on the Team page. Accounts are emailed this table and spreadsheet automatically once every contractor has submitted the month, or use Email Accounts now.
        {rows?.some((r) => r.hourly_rate === null && r.worked > 0) && <span data-testid="hours-no-rate-note" className="ml-1 font-semibold text-amber-600">{rows.filter((r) => r.hourly_rate === null && r.worked > 0).length} contractor(s) with hours have no rate, so the total amount leaves them out.</span>}</p>
    </Layout>
  );
}
